import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/api';
import { AUTH_EXPIRED_EVENT, clearStoredSession, getAuthToken } from '../utils/authSession';

const AuthContext = createContext(null);

const normalizeAuthUser = (userData) => {
  const rawId = userData?.id ?? userData?.user_id ?? userData?.userId;
  const id = typeof rawId === 'string' ? rawId.trim() : '';
  if (!id || !['student', 'doctor'].includes(userData?.role) || userData?.enabled === false) throw new Error('Invalid session user');
  return { ...userData, id };
};
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const revision = useRef(0);
  const sessionToken = useRef(null);
  const restorationController = useRef(null);

  const logout = useCallback(() => {
    revision.current += 1;
    restorationController.current?.abort();
    sessionToken.current = null;
    clearStoredSession();
    setUser(null);
    setToken(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    const restoreStoredSession = () => {
      const currentRevision = ++revision.current;
      restorationController.current?.abort();
      const controller = new AbortController();
      restorationController.current = controller;
      const storedToken = getAuthToken();
      sessionToken.current = storedToken;
      // Unmount the old account's protected UI before validating a replacement.
      // Do not clear storage: another tab may just have signed into that account.
      setUser(null);
      setToken(null);
      setLoading(Boolean(storedToken));
      if (!storedToken) return;

      // Cached user JSON is never trusted for identity, permissions or validity.
      const isCurrent = () => !controller.signal.aborted && currentRevision === revision.current && getAuthToken() === storedToken;
      apiService.getCurrentUser({ signal: controller.signal }).then((freshUser) => {
        if (!isCurrent()) return;
        const verifiedUser = normalizeAuthUser(freshUser);
        setUser(verifiedUser);
        setToken(storedToken);
        try {
          const storage = localStorage.getItem('authToken') ? localStorage : sessionStorage;
          storage.setItem('authUser', JSON.stringify(verifiedUser));
        } catch { /* Session remains usable when caching is unavailable. */ }
      }).catch((error) => {
        if (error.name !== 'AbortError' && isCurrent()) logout();
      }).finally(() => {
        if (isCurrent()) setLoading(false);
      });
    };
    const handleStorageChange = (event) => {
      if (event.key !== null && event.key !== 'authToken') return;
      if (getAuthToken() !== sessionToken.current) restoreStoredSession();
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, logout);
    window.addEventListener('storage', handleStorageChange);
    restoreStoredSession();
    return () => {
      restorationController.current?.abort();
      window.removeEventListener(AUTH_EXPIRED_EVENT, logout);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [logout]);

  const login = (userData, authToken, remember = true) => {
    const verifiedUser = normalizeAuthUser(userData);
    if (typeof authToken !== 'string' || !authToken.trim()) throw new Error('Invalid session token');
    revision.current += 1;
    restorationController.current?.abort();
    clearStoredSession();
    const storage = remember ? localStorage : sessionStorage;
    storage.setItem('authToken', authToken);
    storage.setItem('authUser', JSON.stringify(verifiedUser));
    sessionToken.current = authToken;
    setUser(verifiedUser);
    setToken(authToken);
    setLoading(false);
  };

  const updateUser = useCallback((userData) => {
    const verifiedUser = normalizeAuthUser(userData);
    setUser(verifiedUser);
    try {
      const storage = localStorage.getItem('authToken') ? localStorage : sessionStorage;
      storage.setItem('authUser', JSON.stringify(verifiedUser));
    } catch { /* Best-effort cache. */ }
  }, []);

  return <AuthContext.Provider value={{ user, token, login, logout, updateUser, isAuthenticated: Boolean(token && user), loading }}>{children}</AuthContext.Provider>;
};
